import jsPDF from 'jspdf';
import { domToPng } from 'modern-screenshot';

export interface HeaderInfo {
  logo?: string;
  titulo: string;
  tituloModo: string;
  subtitulo?: string;
  breadcrumb: {
    plano: string;
    pais: string;
    estado: string;
    cidade?: string;
    ano: string;
  };
  ilustracao?: string;
}

export class PdfUtils {
  static async baixarElementoComoPdf(
    elemento: HTMLElement,
    nomeArquivo: string,
    textoComoCitar: string,
    localizacaoSelecionada?: string,
    ano?: string,
    headerInfo?: HeaderInfo
  ): Promise<void> {
    let containerTemporario: HTMLElement | null = null;

    try {
      const nomeCompleto = this.obterNomeArquivo(nomeArquivo, localizacaoSelecionada);

      // Criar container temporário com cabeçalho
      let elementoParaCaptura = elemento;

      if (headerInfo) {
        containerTemporario = this.criarContainerComCabecalho(elemento, headerInfo, textoComoCitar);
        elementoParaCaptura = containerTemporario;
      }

      if (containerTemporario) {
        // Posicionando container temporário visível atrás da interface e aguardar renderização
        containerTemporario.style.position = 'fixed';
        containerTemporario.style.top = '0';
        containerTemporario.style.left = '0';
        containerTemporario.style.visibility = 'visible';
        containerTemporario.style.zIndex = '-1000';
        containerTemporario.style.opacity = '1';

        // Aguardar renderização
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Usando modern-screenshot para captura das tabelas
      const imgData = await domToPng(elementoParaCaptura, {
        scale: 2,
        backgroundColor: '#FAFAFA'
      });

      const pdf = new jsPDF('p', 'mm', 'a4');

      // Cria uma imagem temporária para obter dimensões
      const img = new Image();
      img.src = imgData;
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
      });

      // Cálculo das dimensões do PDF
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (img.height * pdfWidth) / img.width;

      let heightLeft = imgHeight;
      let position = 0;

      // Primeira página
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      // Páginas adicionais
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(nomeCompleto);

    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      throw error;
    } finally {
      // Limpando container temporário
      if (containerTemporario && containerTemporario.parentNode) {
        containerTemporario.parentNode.removeChild(containerTemporario);
      }
    }
  }

  static async baixarTabelasIndividuaisComoPdf(
    tabelas: { elemento: HTMLElement; nome: string }[],
    textoComoCitar: string,
    localizacaoSelecionada?: string,
    ano?: string,
    headerInfo?: HeaderInfo
  ): Promise<void> {
    for (const tabela of tabelas) {
      try {
        await this.baixarElementoComoPdf(
          tabela.elemento,
          tabela.nome,
          textoComoCitar,
          localizacaoSelecionada,
          ano,
          headerInfo
        );

        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Erro ao baixar PDF da tabela ${tabela.nome}:`, error);
      }
    }
  }

  private static obterNomeArquivo(nomeOriginal: string, localizacaoSelecionada?: string, ano?: string): string {
    const extensaoArquivoPdf = '.pdf';
    let nomeArquivo = nomeOriginal;

    if (nomeArquivo.indexOf(extensaoArquivoPdf) === -1) {
      if (localizacaoSelecionada && ano) {
        nomeArquivo += ` - ${localizacaoSelecionada} [${ano}]${extensaoArquivoPdf}`;
      } else {
        nomeArquivo += extensaoArquivoPdf;
      }
    }

    return nomeArquivo;
  }

  private static criarContainerComCabecalho(elemento: HTMLElement, headerInfo: HeaderInfo, textoComoCitar: string): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      background: #FAFAFA;
      width: ${elemento.offsetWidth}px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      visibility: hidden;
      pointer-events: none;
      z-index: -1000;
    `;

    // Chamando método para criar o cabeçalho
    const header = this.criarCabecalho(headerInfo);
    container.appendChild(header);

    const elementoClonado = elemento.cloneNode(true) as HTMLElement;
    container.appendChild(elementoClonado);

    // Seção "Como citar"
    const footer = this.criarFooterComoCitar(headerInfo, textoComoCitar);
    container.appendChild(footer);

    document.body.appendChild(container);

    return container;
  }

  private static criarCabecalho(headerInfo: HeaderInfo): HTMLElement {
    const header = document.createElement('div');

    header.style.cssText = `
      background-color: #FAFAFA;
      padding: 32px 32px 40px 32px;
    `;

    header.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px;">
        <!-- Logo -->
        <div>
          <img src="assets/simcaqv2.svg" alt="SIMCAQ Logo" style="height: 40px;">
        </div>

        <!-- Tag do módulo -->
        <div style="border: 1px solid #ea580c; padding: 6px 12px; border-radius: 999px; width: fit-content;">
          <span style="color: #ea580c; font-size: 14px; font-weight: 500;">
            ${headerInfo.titulo}
          </span>
        </div>
      </div>

      <!-- Breadcrumb visual com ilustração -->
      <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 32px;">
        <!-- Ilustração -->
        ${headerInfo.ilustracao ? `
        <img src="${headerInfo.ilustracao}" alt="ilustration" style="max-width: 118px; height: auto; flex-shrink: 0;">
        ` : ''}

        <div style="display: flex; flex-direction: column; justify-content: space-between; flex: 1; min-width: 0;">
          <div>
            <span style="color: #374151; font-size: 16px;">${headerInfo.breadcrumb.plano}</span>
            <span style="color: #374151; font-size: 16px;"> /</span>
            <br>
            <span style="color: #374151; font-size: 16px;">${headerInfo.breadcrumb.pais} </span>
            <span style="color: #374151; font-size: 16px;">/ </span>
            <span style="color: #374151; font-size: 16px;">${headerInfo.breadcrumb.estado}</span>
          </div>

          ${headerInfo.breadcrumb.cidade ? `
          <div style="overflow-wrap: break-word; word-break: break-word;">
            <span style="color: #7FA6BF; font-size: 18px; font-weight: bold;">${headerInfo.breadcrumb.cidade} ${headerInfo.breadcrumb.ano}</span>
          </div>
          ` : ''}
        </div>
      </div>

      <!-- Seção de resultados -->
      <div style="margin-bottom: 0;">
        <h1 style="color: #374151; font-size: 20px; font-weight: bold; margin: 0 0 8px 0;">${headerInfo.tituloModo}</h1>
        <p style="color: #374151; font-size: 16px; margin: 0;">${headerInfo.subtitulo}</p>
      </div>
    `;

    return header;
  }

  private static criarFooterComoCitar(headerInfo: HeaderInfo, textoComoCitar: string): HTMLElement {
    const footer = document.createElement('div');
    footer.style.cssText = `
      background: #FAFAFA;
      padding: 64px 32px 32px 32px;
    `;

    // Gerar data atual formatada
    const dataAtual = new Date();
    const dataFormatada = dataAtual.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/\./g, '');

    footer.innerHTML = `
      <div style="width: 100%;">
        <div style="color: #ea580c; font-weight: bold; font-size: 20px;">
          Como citar esta página?
        </div>
        <div style="width: 100%; padding-top: 16px;">
          <div style="width: 100%; font-size: 16px; color: #4b5563; padding: 8px; border-top-left-radius: 4px; border-bottom-left-radius: 4px;">
            SIMULADOR de Custo-Aluno Qualidade: ${textoComoCitar}, 2026. Disponível em: &lt;https://simcaq.ufg.br/&gt;. Acesso em: ${dataFormatada}.
          </div>
        </div>
      </div>
    `;

    return footer;
  }

}
